import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Check, Pencil, Save, X, Loader2, AlertTriangle, ReceiptText } from "lucide-react";
import { API, authFetch } from "@/App";

/**
 * Modal de validation des données extraites - Format tableau 3 colonnes
 * 
 * Mode "extraction" (1ère extraction) : Affiche uniquement les zones alimentées, une seule colonne de valeur
 * Mode "enrichissement" : Affiche TOUTES les zones avec 1ER EMAIL | 2EME EMAIL
 */
export function ExtractionValidationModal({
  isOpen,
  onClose,
  mode = "extraction", // "extraction" ou "enrichissement"
  extractedData = {},      // Nouvelles données extraites (2ème email)
  existingData = {},       // Données existantes (1er email)
  orderId = null,
  onSave,
  isLoading = false,
  potentialDuplicates = [],
  invoiceTotals = null
}) {
  // État local pour les données validées et les choix
  const [validatedData, setValidatedData] = useState({});
  const [selectedColumn, setSelectedColumn] = useState({}); // { fieldName: 1 | 2 | 'custom' }
  const [customValues, setCustomValues] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);

  // Champs qui doivent par défaut garder la valeur du 1er email (commande)
  const firstEmailDefaultFields = [
    'content', 'type', 'order_status', 'order status', 'color', 
    'supplier', 'manufacturer', 'name', 'client', 'phone', 'email', 'address'
  ];

  // Champs qui doivent par défaut prendre la valeur du 2ème email (paiement)
  const secondEmailDefaultFields = [
    'date_of_payment', 'date of payment', 'payment_method', 'payment method',
    'date_of_payment_confirm', 'date of payment confirm', 'statut_reglement', 'statut reglement'
  ];

  // Convertir un nom de colonne en clé (même logique que Tableau.jsx)
  const getColumnKey = (colName) => {
    return colName.toLowerCase().replace(/\s+/g, '_').replace(/'/g, '').replace(/-/g, '_').replace(/\./g, '_');
  };

  // Formatage des dates ISO (YYYY-MM-DD) en format français (JJ/MM/AAAA)
  const formatDateDisplay = (value, columnName) => {
    if (!value) return value;
    
    // Vérifier si c'est une colonne de date
    const dateColumns = ['date_of_order', 'date of order', 'date_of_payment', 'date of payment', 
                         'date_of_payment_confirm', 'date of payment confirm', 'delivery_date'];
    const isDateColumn = dateColumns.some(dc => columnName.toLowerCase().includes(dc.replace(/_/g, ' ').toLowerCase()) ||
                                                 columnName.toLowerCase().includes(dc.toLowerCase()));
    
    if (!isDateColumn) return value;
    
    const str = String(value).trim();
    
    // Format: YYYY-MM-DD (ISO) -> DD/MM/YYYY
    const isoMatch = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:\s|T|$)/);
    if (isoMatch) {
      const year = isoMatch[1];
      const month = isoMatch[2].padStart(2, '0');
      const day = isoMatch[3].padStart(2, '0');
      return `${day}/${month}/${year}`;
    }
    
    // Format: DD.MM.YYYY (German) -> DD/MM/YYYY
    const germanMatch = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (germanMatch) {
      const day = germanMatch[1].padStart(2, '0');
      const month = germanMatch[2].padStart(2, '0');
      const year = germanMatch[3];
      return `${day}/${month}/${year}`;
    }
    
    return value;
  };

  // Récupérer la valeur d'un champ depuis les données, en essayant plusieurs formats de clé
  const getValueFromData = (data, columnName) => {
    if (!data) return null;
    
    const key = getColumnKey(columnName);
    
    // Essayer dans l'ordre : nom exact, clé normalisée, variations
    const keysToTry = [
      columnName,
      key,
      columnName.toUpperCase(),
      columnName.toLowerCase(),
    ];
    
    for (const k of keysToTry) {
      if (data[k] !== undefined && data[k] !== null) {
        const val = String(data[k]).trim();
        if (val && val !== '-') {
          return data[k];
        }
      }
    }
    
    return null;
  };

  // Détermine le choix par défaut pour un champ
  const getDefaultChoice = (fieldName, val1, val2) => {
    const fieldLower = fieldName.toLowerCase().replace(/[._]/g, ' ');
    
    // Si une seule valeur existe, la choisir
    const hasVal1 = val1 && String(val1).trim() && String(val1).trim() !== '-';
    const hasVal2 = val2 && String(val2).trim() && String(val2).trim() !== '-';
    
    if (hasVal1 && !hasVal2) return 1;
    if (!hasVal1 && hasVal2) return 2;
    if (!hasVal1 && !hasVal2) return 1;
    
    // Les deux ont des valeurs - appliquer la règle par défaut
    if (firstEmailDefaultFields.some(f => fieldLower.includes(f))) {
      return 1;
    }
    if (secondEmailDefaultFields.some(f => fieldLower.includes(f))) {
      return 2;
    }
    
    // Par défaut, garder la valeur du 1er email si elle existe
    return 1;
  };

  // Charger les colonnes du tableau
  useEffect(() => {
    if (isOpen) {
      setIsLoadingColumns(true);
      authFetch(`${API}/columns`, {})
        .then(res => res.json())
        .then(data => {
          if (data.columns && Array.isArray(data.columns)) {
            setTableColumns(data.columns);
          }
        })
        .catch(err => console.error("Failed to load columns:", err))
        .finally(() => setIsLoadingColumns(false));
    }
  }, [isOpen]);

  // Initialiser les choix quand le modal s'ouvre et les colonnes sont chargées
  useEffect(() => {
    if (isOpen && tableColumns.length > 0) {
      const initialSelected = {};
      const initialValidated = {};
      
      for (const col of tableColumns) {
        const fieldName = col.name;
        const val1 = getValueFromData(existingData, fieldName);
        const val2 = getValueFromData(extractedData, fieldName);
        const defaultChoice = mode === "extraction" ? 2 : getDefaultChoice(fieldName, val1, val2);
        
        initialSelected[fieldName] = defaultChoice;
        initialValidated[fieldName] = defaultChoice === 1 ? val1 : val2;
      }
      
      setSelectedColumn(initialSelected);
      setValidatedData(initialValidated);
      setCustomValues({});
      setEditingField(null);
    }
  }, [isOpen, tableColumns, mode, JSON.stringify(existingData), JSON.stringify(extractedData)]);

  // Gérer le clic sur une colonne
  const handleSelectColumn = (field, column) => {
    const val1 = getValueFromData(existingData, field);
    const val2 = getValueFromData(extractedData, field);
    
    setSelectedColumn(prev => ({ ...prev, [field]: column }));
    
    if (column === 1) {
      setValidatedData(prev => ({ ...prev, [field]: val1 }));
    } else if (column === 2) {
      setValidatedData(prev => ({ ...prev, [field]: val2 }));
    } else if (column === 'custom') {
      setValidatedData(prev => ({ ...prev, [field]: customValues[field] || '' }));
    }
  };

  // Gérer l'édition personnalisée
  const handleStartEdit = (field) => {
    setEditingField(field);
    const currentValue = validatedData[field] || '';
    setCustomValues(prev => ({ ...prev, [field]: currentValue }));
  };

  const handleSaveEdit = (field) => {
    setValidatedData(prev => ({ ...prev, [field]: customValues[field] }));
    setSelectedColumn(prev => ({ ...prev, [field]: 'custom' }));
    setEditingField(null);
  };

  const handleCancelEdit = () => {
    setEditingField(null);
  };

  // Préparer les données pour la sauvegarde (avec les clés normalisées)
  const prepareDataForSave = (data) => {
    const result = {};
    for (const col of tableColumns) {
      const fieldName = col.name;
      const key = getColumnKey(fieldName);
      const value = data[fieldName];
      
      if (value !== null && value !== undefined) {
        // Sauvegarder avec la clé normalisée (pour le tableau)
        result[key] = value;
        // Aussi avec le nom original pour compatibilité
        result[fieldName] = value;
      }
    }
    return result;
  };

  // Enregistrer
  const handleSave = () => {
    // Commit toute édition en cours (crayon ouvert sans clic sur ✓)
    let dataForSave = validatedData;
    if (editingField !== null && customValues[editingField] !== undefined) {
      dataForSave = { ...validatedData, [editingField]: customValues[editingField] };
      setValidatedData(dataForSave);
      setSelectedColumn(prev => ({ ...prev, [editingField]: 'custom' }));
      setEditingField(null);
    }
    const dataToSave = prepareDataForSave(dataForSave);
    onSave(dataToSave, orderId);
  };

  // Obtenir la valeur affichée (tronquée si trop longue)
  const getDisplayValue = (value, maxLength = 100) => {
    if (value === null || value === undefined) return <span className="text-zinc-400 italic">vide</span>;
    const str = String(value).trim();
    if (!str || str === '-') return <span className="text-zinc-400 italic">vide</span>;
    if (str.length > maxLength) return str.substring(0, maxLength) + '...';
    return str;
  };

  // Vérifier si deux valeurs sont différentes (conflit)
  const isConflict = (val1, val2) => {
    const str1 = val1 ? String(val1).trim() : '';
    const str2 = val2 ? String(val2).trim() : '';
    const hasVal1 = str1 && str1 !== '-';
    const hasVal2 = str2 && str2 !== '-';
    return hasVal1 && hasVal2 && str1 !== str2;
  };

  // Loading state
  if (isLoadingColumns) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            <span className="ml-2">Chargement des colonnes...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Mode extraction simple (1ère extraction)
  if (mode === "extraction") {
    const filledFields = tableColumns.filter(col => {
      const val = getValueFromData(extractedData, col.name);
      return val !== null;
    });

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="w-5 h-5" />
              Validation des données extraites
            </DialogTitle>
            <p className="text-sm text-zinc-500">
              Vérifiez les données extraites avant de créer la commande.
            </p>
          </DialogHeader>

          {potentialDuplicates.length > 0 && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="duplicate-warning-banner">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">Une commande similaire existe déjà dans le tableau :</p>
                {potentialDuplicates.map((d) => (
                  <p key={d.order_id} className="mt-0.5">
                    {d.name} — {d.date}
                    {(d.status || d.created_at) && (
                      <span className="text-amber-600">
                        {" "}({[d.type, d.status, d.created_at ? `créée le ${new Date(d.created_at).toLocaleDateString("fr-FR")}` : ""].filter(Boolean).join(", ")})
                      </span>
                    )}
                  </p>
                ))}
                <p className="mt-1 text-xs text-amber-600">Vous pouvez tout de même créer la commande s'il s'agit bien d'une nouvelle commande.</p>
              </div>
            </div>
          )}

          {(() => {
            if (!invoiceTotals) return null;
            const { subtotal, tva, total_ttc } = invoiceTotals;
            if (!subtotal && !tva && !total_ttc) return null;
            const toNum = (v) => (v === null || v === undefined || v === "" ? null : parseFloat(String(v).replace(",", ".")));
            const fmtE = (v) => {
              const n = toNum(v);
              if (n === null || isNaN(n)) return "—";
              return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
            };
            const s = toNum(subtotal), t = toNum(tva), tt = toNum(total_ttc);
            const coherent = s !== null && t !== null && tt !== null ? Math.abs(s + t - tt) < 0.02 : null;
            return (
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="invoice-totals-preview">
                <div className="flex items-center gap-2 mb-2.5">
                  <ReceiptText className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-semibold text-zinc-700">Aperçu facture détecté</span>
                  {coherent !== null && (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full ${coherent ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
                      data-testid="invoice-totals-coherence"
                    >
                      {coherent ? "cohérent (HT + TVA = TTC)" : "à vérifier (HT + TVA ≠ TTC)"}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div data-testid="invoice-subtotal">
                    <div className="text-xs text-zinc-500 mb-0.5">Sous-total HT</div>
                    <div className="text-sm font-medium text-zinc-800">{fmtE(subtotal)}</div>
                  </div>
                  <div data-testid="invoice-tva">
                    <div className="text-xs text-zinc-500 mb-0.5">TVA</div>
                    <div className="text-sm font-medium text-zinc-800">{fmtE(tva)}</div>
                  </div>
                  <div data-testid="invoice-total-ttc">
                    <div className="text-xs text-zinc-500 mb-0.5">Total TTC</div>
                    <div className="text-sm font-bold text-orange-600">{fmtE(total_ttc)}</div>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="flex-1 overflow-y-auto py-4">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-white">
                <tr>
                  <th className="text-left p-2 border-b-2 border-zinc-200 font-semibold text-zinc-700 w-1/3">CHAMP</th>
                  <th className="text-left p-2 border-b-2 border-zinc-200 font-semibold text-zinc-700">VALEUR</th>
                  <th className="text-center p-2 border-b-2 border-zinc-200 font-semibold text-zinc-700 w-20">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {filledFields.map((col) => {
                  const field = col.name;
                  const value = validatedData[field];
                  const isEditing = editingField === field;
                  const isLongText = String(value || '').length > 100;

                  return (
                    <tr key={field} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="p-2 font-medium text-sm text-zinc-600">{field}</td>
                      <td className="p-2">
                        {isEditing ? (
                          <div className="flex gap-2">
                            {isLongText ? (
                              <Textarea
                                value={customValues[field] || ''}
                                onChange={(e) => setCustomValues(prev => ({ ...prev, [field]: e.target.value }))}
                                className="min-h-[80px] text-sm flex-1"
                                autoFocus
                              />
                            ) : (
                              <Input
                                value={customValues[field] || ''}
                                onChange={(e) => setCustomValues(prev => ({ ...prev, [field]: e.target.value }))}
                                className="text-sm flex-1"
                                autoFocus
                              />
                            )}
                            <div className="flex flex-col gap-1">
                              <Button size="sm" onClick={() => handleSaveEdit(field)} className="h-7 px-2">
                                <Check className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={handleCancelEdit} className="h-7 px-2">
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-zinc-700 whitespace-pre-wrap break-words">
                            {getDisplayValue(formatDateDisplay(value, field), 200)}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {!isEditing && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleStartEdit(field)}
                            className="h-7 px-2"
                            title="Modifier"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? "Enregistrement..." : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Créer la commande
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Mode enrichissement (2ème email) - Format 3 colonnes
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <Check className="w-5 h-5" />
            Validation de l'enrichissement
          </DialogTitle>
          <p className="text-sm text-zinc-500">
            Cliquez sur la colonne à conserver pour chaque champ. Vous pouvez aussi saisir une valeur personnalisée.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className="text-left p-2 border-b-2 border-zinc-300 font-semibold text-zinc-700 bg-zinc-100 w-1/4">
                  CHAMP
                </th>
                <th className="text-left p-2 border-b-2 border-zinc-300 font-semibold text-blue-700 bg-blue-50 w-1/3">
                  1ER EMAIL (Commande)
                </th>
                <th className="text-left p-2 border-b-2 border-zinc-300 font-semibold text-orange-700 bg-orange-50 w-1/3">
                  2EME EMAIL (Règlement)
                </th>
                <th className="text-center p-2 border-b-2 border-zinc-300 font-semibold text-zinc-700 bg-zinc-100 w-16">
                  
                </th>
              </tr>
            </thead>
            <tbody>
              {tableColumns.map((col) => {
                const field = col.name;
                const val1 = getValueFromData(existingData, field);
                const val2 = getValueFromData(extractedData, field);
                const selected = selectedColumn[field];
                const hasConflict = isConflict(val1, val2);
                const isEditing = editingField === field;

                return (
                  <tr 
                    key={field} 
                    className={`border-b border-zinc-100 ${hasConflict ? 'bg-amber-50' : 'hover:bg-zinc-50'}`}
                  >
                    {/* Colonne CHAMP */}
                    <td className="p-2 font-medium text-sm text-zinc-600 align-top">
                      <div className="flex items-center gap-2">
                        {field}
                        {hasConflict && (
                          <span className="text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">
                            Conflit
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Colonne 1ER EMAIL */}
                    <td 
                      className={`p-2 cursor-pointer align-top transition-colors ${
                        selected === 1 
                          ? 'bg-blue-100 border-2 border-blue-400' 
                          : 'hover:bg-blue-50 border-2 border-transparent'
                      }`}
                      onClick={() => !isEditing && handleSelectColumn(field, 1)}
                    >
                      <div className="flex items-start gap-2">
                        {selected === 1 && (
                          <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        )}
                        <span className="text-sm text-zinc-700 whitespace-pre-wrap break-words">
                          {getDisplayValue(formatDateDisplay(val1, field), 150)}
                        </span>
                      </div>
                    </td>

                    {/* Colonne 2EME EMAIL */}
                    <td 
                      className={`p-2 cursor-pointer align-top transition-colors ${
                        selected === 2 
                          ? 'bg-orange-100 border-2 border-orange-400' 
                          : 'hover:bg-orange-50 border-2 border-transparent'
                      }`}
                      onClick={() => !isEditing && handleSelectColumn(field, 2)}
                    >
                      <div className="flex items-start gap-2">
                        {selected === 2 && (
                          <Check className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                        )}
                        <span className="text-sm text-zinc-700 whitespace-pre-wrap break-words">
                          {getDisplayValue(formatDateDisplay(val2, field), 150)}
                        </span>
                      </div>
                    </td>

                    {/* Colonne ACTION */}
                    <td className="p-2 text-center align-top">
                      {isEditing ? (
                        <div className="flex flex-col gap-1">
                          <Button size="sm" onClick={() => handleSaveEdit(field)} className="h-7 px-2">
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={handleCancelEdit} className="h-7 px-2">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStartEdit(field)}
                          className={`h-7 px-2 ${selected === 'custom' ? 'text-purple-600' : ''}`}
                          title="Saisir une valeur personnalisée"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Zone d'édition personnalisée */}
        {editingField && (
          <div className="border-t pt-4 pb-2 bg-purple-50 px-4 -mx-6 mb-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium text-purple-700">Valeur personnalisée pour : {editingField}</span>
            </div>
            <div className="flex gap-2">
              {String(validatedData[editingField] || '').length > 100 ? (
                <Textarea
                  value={customValues[editingField] || ''}
                  onChange={(e) => setCustomValues(prev => ({ ...prev, [editingField]: e.target.value }))}
                  className="min-h-[80px] text-sm flex-1 bg-white"
                  autoFocus
                />
              ) : (
                <Input
                  value={customValues[editingField] || ''}
                  onChange={(e) => setCustomValues(prev => ({ ...prev, [editingField]: e.target.value }))}
                  className="text-sm flex-1 bg-white"
                  autoFocus
                />
              )}
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-4">
          <div className="flex items-center gap-4 mr-auto text-sm text-zinc-500">
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-100 border border-blue-400 rounded"></div>
              1er email sélectionné
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-orange-100 border border-orange-400 rounded"></div>
              2ème email sélectionné
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-amber-200 rounded"></div>
              Conflit
            </span>
          </div>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Enregistrement..." : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Mettre à jour
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
